/**
 * Discriminate authentication failures from infrastructure/API failures.
 * Used so a 5xx/network error never clears the JWT / redirects to /auth.
 */

/**
 * @param {unknown} err Axios or Error-like value.
 * @returns {boolean} True when the failure is an auth/session problem.
 */
export function isAuthFailure(err) {
  if (!err) return false;

  const status = err?.response?.status;
  const data = err?.response?.data;
  const extCode =
    data?.extensions?.code ||
    data?.extensions?.exception?.code ||
    (Array.isArray(data?.errors) ? data.errors[0]?.extensions?.code : undefined);
  const msg = String(
    err?.message ||
      data?.message ||
      (Array.isArray(data?.errors) ? data.errors.map((e) => e?.message).join(" ") : "") ||
      ""
  ).toLowerCase();

  if (status === 401 || status === 403) return true;
  if (extCode === "UNAUTHENTICATED" || extCode === "FORBIDDEN") return true;

  // Explicit auth messages (including legacy middleware that used HTTP 500).
  if (
    /not authorised|not authorized|token is expired|unauthenticated|jwt malformed|invalid token|jwt expired/i.test(
      msg
    )
  ) {
    return true;
  }

  return false;
}

/**
 * Clear JWTs and redirect to the auth MFE. Call only after isAuthFailure(err).
 *
 * @param {string} [authEndpoint]
 * @returns {void}
 */
export function clearSessionAndRedirect(authEndpoint = process.env.REACT_APP_AUTH_ENDPOINT) {
  try {
    localStorage.removeItem("jwt");
    localStorage.removeItem("jwt-ghost");
  } catch {
    // ignore storage errors
  }
  if (authEndpoint && typeof window !== "undefined") {
    const authBase = String(authEndpoint).replace(/\/$/, "");
    const next = encodeURIComponent(window.location.href);
    window.location.replace(`${authBase}/login?next=${next}`);
  }
}
